import React, {
  useState,
  useRef,
  useEffect,
  useLayoutEffect,
  forwardRef,
  useCallback,
  useMemo,
} from 'react';
import Draggable from 'react-draggable';
import { Resizable } from 'react-resizable';
import { flushSync } from 'react-dom';
import { Link, useParams } from 'react-router-dom';

import {
  datasetService,
  datasetLineageService,
  fieldService,
  relationshipService,
} from '../../../services';
import { useMessage, useTooltip, useQueryParams, useGlobalState } from '../../../hooks';
import {
  Asset,
  DatasetSubtypeOperation,
  formatDatasetSegment,
  getHierarchyPath,
  handleError,
  technicalDefinitionLanguage,
  updateFieldsWithRelationships,
} from '../../../helpers';

import {
  Checkbox,
  DataTable,
  Icon,
  Pill,
  SelectButtons,
  textareaToPlainText,
  TechnicalDefinition,
} from '../../';

import './PopupTable.css';

export const PopupTable = React.memo(
  ({
    item,
    index,
    graph,
    fields,
    setFields,
    datasetDefinition,
    setDatasetDefinitions,
    datasetLineage,
    setDatasetLineage,
    datasetRelationships,
    setDatasetRelationships,
    onMouseEnterRow,
    onMouseLeaveRow,
    togglePopup,
    showPopups,
  }) => {
    const { setHistory, queryParams } = useQueryParams();

    const defaultPosition = {
      x:
        (queryParams?.popupTableItems && queryParams?.popupTableItems[item.nodeId]?.x) ||
        index * 20,
      y: (queryParams?.popupTableItems && queryParams?.popupTableItems[item.nodeId]?.y) || 0,
      width:
        (queryParams?.popupTableItems && queryParams?.popupTableItems[item.nodeId]?.width) ||
        undefined,
      height:
        (queryParams?.popupTableItems && queryParams?.popupTableItems[item.nodeId]?.height) ||
        undefined,
    };

    const datasetId = parseInt(item.nodeId, 10);

    let viewModes = [];
    let viewModeLocalStorageItem;
    let defaultViewMode = 'fields';

    switch (graph) {
      case 'lineage':
        viewModes = [
          { icon: 'editor_table', value: 'fields', tooltip: 'Show fields' },
          {
            icon: 'editor_relationships',
            value: 'relationships',
            tooltip: 'Show related datasets',
          },
          {
            icon: 'editor_lineage',
            value: 'lineage',
            tooltip: 'Show upstream and downstream datasets',
          },
          {
            icon: 'editor_code',
            value: 'technical_definition',
            tooltip: 'Show technical definition',
          },
        ];
        viewModeLocalStorageItem = 'LineagePopupTableViewMode';
        break;
      case 'relationships':
        viewModes = [
          { icon: 'editor_table', value: 'fields', tooltip: 'Show fields' },
          {
            icon: 'editor_relationships',
            value: 'relationships',
            tooltip: 'Show related datasets',
          },
          {
            icon: 'editor_lineage',
            value: 'lineage',
            tooltip: 'Show upstream and downstream datasets',
          },
          {
            icon: 'editor_code',
            value: 'technical_definition',
            tooltip: 'Show technical definition',
          },
        ];
        viewModeLocalStorageItem = 'RelationshipPopupTableViewMode';
        break;
      default:
      // nothing
    }

    const [position, setPosition] = useState(defaultPosition);
    const [viewMode, setViewMode] = useState(
      localStorage.getItem(viewModeLocalStorageItem) || defaultViewMode
    );
    const [showDescription, setShowDescription] = useState(
      localStorage.getItem('PopupTableShowDescription') === 'true' ? true : false
    );
    const [loadingTable, setLoadingTable] = useState(false);

    const { showMessage } = useMessage();
    const { showTooltip, hideTooltip } = useTooltip();
    const { dataset_id } = useParams();

    const nodeRef = useRef();
    const tableRef = useRef();
    const descriptionRef = useRef();
    const technicalDefinitionRef = useRef();
    const headerRef = useRef();
    const titleRef = useRef();

    useEffect(() => {
      viewMode === 'fields' && fetchFields();
      viewMode === 'technical_definition' && fetchDataset();
      viewMode === 'lineage' && fetchDatasetLineage();
      viewMode === 'relationships' && fetchDatasetRelationships();
    }, [item]);

    useLayoutEffect(() => {
      handleResize(undefined, { size: {} }, true);
    }, [item, showPopups]);

    const fetchFields = async () => {
      if (!fields) {
        setLoadingTable(true);

        const promises = [
          fieldService
            .getByDatasetId(datasetId)
            .then(res => {
              // This displays results faster, but causes the field list to re-sort when the relationships data has loaded.
              //res?.fields && setFields({datasetId: datasetId, fields: res.fields})
              //setLoadingTable(false)
              return res;
            })
            .catch(err => {
              throw err;
            }),
          relationshipService
            .getByObjectId(Asset.Dataset, datasetId)
            .then(result => {
              result &&
                setDatasetRelationships(
                  {
                    nodes: [],
                    edges: result.relationships,
                  },
                  datasetId
                );

              return result;
            })
            .catch(err => {
              throw err;
            }),
        ];

        await Promise.all(promises)
          .then(result => {
            const fieldsWithKeys = updateFieldsWithRelationships({
              fields: result[0].fields,
              relationships: result[1].relationships,
            });

            // Attach each field's hierarchical path (parent fields), shown as the field name's
            // tooltip subtitle — matching the Dataset view's Fields table.
            const fieldsWithPath = fieldsWithKeys.map(x => ({
              ...x,
              field_path: getHierarchyPath(x, fieldsWithKeys),
            }));

            setFields({ datasetId: datasetId, fields: fieldsWithPath });
          })
          .catch(err => {
            handleError({ err, showMessage });
          })
          .finally(res => {
            setLoadingTable(false);
          });
      }
    };

    const fetchDataset = async () => {
      if (!datasetDefinition) {
        await datasetService
          .getById(datasetId, undefined, false)
          .then(res => {
            const ds = res.datasets.length > 0 && res.datasets[0];
            ds &&
              setDatasetDefinitions(prev => [
                ...prev,
                {
                  datasetId: datasetId,
                  dataset_technical_definition: ds.dataset_technical_definition,
                  dataset_technical_definition_raw: ds.dataset_technical_definition_raw,
                },
              ]);
          })
          .catch(err => {
            showMessage(err, 'error');
          });
      }
    };

    const fetchDatasetLineage = async () => {
      let promises = [];

      if (!(datasetLineage?.datasetId === datasetId)) {
        setLoadingTable(true);

        promises.push(
          await datasetLineageService.getByObjectId(Asset.Dataset, datasetId).catch(err => {
            showMessage(err, 'error');
          })
        );

        promises.push(
          await datasetLineageService.getRelatedDatasets(datasetId).catch(err => {
            showMessage(err, 'error');
          })
        );

        Promise.all(promises)
          .then(result => {
            result &&
              setDatasetLineage(
                { nodes: result[1].datasets, edges: result[0].dataset_lineage },
                datasetId
              );
          })
          .catch(err => {
            // Errors should be handled in each promise
          })
          .finally(res => {
            setLoadingTable(false);
          });
      }
    };

    const fetchDatasetRelationships = async () => {
      let promises = [];

      if (!(datasetRelationships?.datasetId === datasetId)) {
        setLoadingTable(true);

        promises.push(
          await relationshipService.getByObjectId(Asset.Dataset, datasetId).catch(err => {
            showMessage(err, 'error');
          })
        );

        //	    promises.push(
        //	      await relationshipService.getRelatedDatasets(datasetId)
        //	      .catch(err => {
        //	        showMessage(err, 'error')
        //	      })
        //	    )

        Promise.all(promises)
          .then(result => {
            result &&
              setDatasetRelationships(
                {
                  nodes: [], //result[1].datasets,
                  edges: result[0].relationships,
                },
                datasetId
              );
          })
          .catch(err => {
            // Errors should be handled in each promise
          })
          .finally(res => {
            setLoadingTable(false);
          });
      }
    };

    const handleViewModeChange = event => {
      const mode = event.value;

      if (mode === 'technical_definition') {
        fetchDataset();
      } else if (mode === 'fields') {
        fetchFields();
      } else if (mode === 'lineage') {
        fetchDatasetLineage();
      } else if (mode === 'relationships') {
        fetchDatasetRelationships();
      }

      setViewMode(mode);

      // Persist state in local storage
      localStorage.setItem(viewModeLocalStorageItem, mode);
    };

    const toggleShowDescription = event => {
      setShowDescription(!showDescription);

      // Persist state in local storage
      localStorage.setItem('PopupTableShowDescription', !showDescription);
    };

    const handleResize = (event, { node, size, handle }, initialLoad) => {
      const current = nodeRef.current;

      if (!current) return;

      const parent = current.offsetParent;
      const parentWidth = parent.offsetWidth;
      const parentHeight = parent.offsetHeight;

      let newWidth = isNaN(size.width)
        ? initialLoad
          ? position.width
          : current.offsetWidth
        : size.width;
      let newHeight = isNaN(size.height)
        ? initialLoad
          ? position.height
          : current.offsetHeight
        : size.height;
      let newX = position.x;
      let newY = position.y;

      // Set max width to viewport
      if (position.x + size.width > parentWidth) {
        newWidth = position.width;
      }

      // Set max height to viewport
      if (position.y + size.height > parentHeight) {
        newHeight = position.height;
      }

      // Set width on title
      if (titleRef.current) {
        let newTitleWidth = newWidth - 261;
        if (newTitleWidth > titleRef.current.children[0].offsetWidth) {
          newTitleWidth = titleRef.current.children[0].offsetWidth + 1;
        }
        titleRef.current.style.width = newTitleWidth + 'px';
      }

      // Set height on technical definition
      if (technicalDefinitionRef.current) {
        technicalDefinitionRef.current.style.minHeight = current.offsetHeight - 50 + 'px';
      }

      setPosition(prev => ({
        x: newX,
        y: newY,
        width: newWidth,
        height: newHeight,
      }));
    };

    const handleResizeStop = (event, { node, size, handle }) => {
      const oldPopupTableItems = queryParams.popupTableItems || {};
      let newPopupTableItems = { ...oldPopupTableItems };

      newPopupTableItems[item.nodeId] = {
        ...newPopupTableItems[item.nodeId],
        width: size.width,
        height: size.height,
      };

      setHistory({ popupTableItems: newPopupTableItems });
    };

    const handleDragStop = (event, { x, y }) => {
      const oldPopupTableItems = queryParams.popupTableItems || {};
      let newPopupTableItems = { ...oldPopupTableItems };

      newPopupTableItems[item.nodeId] = {
        ...newPopupTableItems[item.nodeId],
        x: x,
        y: y,
      };

      setHistory({ popupTableItems: newPopupTableItems });
    };

    const handleDrag = (event, { x, y }) => {
      setPosition(prev => ({
        ...prev,
        x: x,
        y: y,
      }));
    };

    const getFieldTableColumns = useCallback(() => {
      let columns = [
        {
          id: 'field_name',
          name: 'Field',
          className: showDescription && 'bold',
          link: '/systems/:system_id/:datasource_id/:dataset_group_id/:dataset_id/:field_id',
          tooltip: 'attribute_description',
          tooltipSubtitle: 'field_path',
          // Key-type + datatype icons are inline within the primary column so the expand toggle and
          // depth indentation (both rendered on the primary column) align with nested fields,
          // matching the Dataset view's Fields table.
          inline: [
            { type: 'icon', icon: 'key_type', tooltip: 'key_list' },
            { type: 'icon', icon: 'datatype_category', tooltip: 'datatype_fullname' },
          ],
        },
        //{id: 'is_nullable', name: '', tooltip: 'is_nullable'},
      ];
      if (showDescription) {
        columns.push({
          id: 'attribute_description',
          name: 'Description',
          type: 'description',
        });
      }
      return columns;
    });

    const getUpstreamTableColumns = useCallback(() => {
      let columns = [
        { id: 'dataset_type_name', name: '', type: 'icon', tooltip: 'dataset_type_name' },
        {
          id: 'label',
          name: 'Dataset',
          className: showDescription && 'bold',
          link: '/systems/:system_id/:datasource_id/:dataset_group_id/:id?tab=lineage',
          tooltip: 'dataset_description',
        },
      ];
      if (showDescription) {
        columns.push({ id: 'dataset_description', name: 'Description', type: 'description' });
      }
      return columns;
    });

    const getDownstreamTableColumns = useCallback(() => {
      let columns = [
        { id: 'dataset_type_name', name: '', type: 'icon', tooltip: 'dataset_type_name' },
        {
          id: 'label',
          name: 'Dataset',
          className: showDescription && 'bold',
          link: '/systems/:system_id/:datasource_id/:dataset_group_id/:id?tab=lineage',
          tooltip: 'dataset_description',
        },
      ];
      if (showDescription) {
        columns.push({ id: 'dataset_description', name: 'Description', type: 'description' });
      }
      return columns;
    });

    const getRelationshipsColumns = useCallback(() => {
      let columns = [
        { id: 'datatype_category', name: '', type: 'icon', tooltip: 'datatype_fullname' },
        {
          id: 'field_name',
          name: 'Field',
          link: '/systems/:system_id/:datasource_id/:dataset_group_id/:dataset_id/:field_id',
          tooltip: 'attribute_description',
          className: 'bold',
        },
        { id: 'direction', name: '', type: 'icon' },
        {
          id: 'related_datatype_category',
          name: '',
          type: 'icon',
          tooltip: 'related_datatype_fullname',
        },
        {
          id: 'related_field_name',
          name: 'Related Field',
          link: '/systems/:related_system_id/:related_datasource_id/:related_dataset_group_id/:related_dataset_id/:related_field_id',
          tooltip: 'related_attribute_description',
        },
        {
          id: 'related_dataset_type_name',
          name: '',
          isGrouped: true,
          type: 'icon',
          tooltip: 'related_dataset_type_name',
        },
        {
          id: 'related_dataset_name',
          name: 'Related Dataset',
          isGrouped: true,
          link: '/systems/:related_system_id/:related_datasource_id/:related_dataset_group_id/:related_dataset_id?tab=relationships',
          tooltip: 'related_dataset_description',
        },
      ];
      if (showDescription) {
        columns.push({
          id: 'related_dataset_description',
          name: 'Related Dataset Description',
          isGrouped: true,
          type: 'description',
        });
      }
      return columns;
    });

    const upstreamLineage = useMemo(() => {
      return datasetLineage?.neighborNodes.upstream;
    });

    const downstreamLineage = useMemo(() => {
      return datasetLineage?.neighborNodes.downstream;
    });

    const data = item.data;

    let showPopup = showPopups[item.nodeId] !== undefined ? showPopups[item.nodeId] : true;

    if (!showPopup) {
      return;
    }

    const getTabForUrl = () => {
      return ['relationships', 'lineage'].includes(viewMode) ? viewMode : graph;
    };

    return (
      <Draggable
        handle=".PopupTable"
        defaultPosition={{ x: 0, y: 0 }}
        bounds="parent"
        position={{ x: position.x, y: position.y }}
        onDrag={handleDrag}
        onStop={handleDragStop}
        nodeRef={nodeRef}
        cancel=".link, .react-resizable-handle, .CustomTextareaCode"
      >
        <Resizable
          height={position.height || nodeRef?.current?.offsetHeight}
          width={position.width || nodeRef?.current?.offsetWidth}
          resizeHandles={['se']}
          onResize={handleResize}
          onResizeStop={handleResizeStop}
        >
          <div
            className="PopupTable"
            ref={nodeRef}
            style={{
              width: position.width ? position.width + 'px' : 'initial',
              height: position.height ? position.height + 'px' : 'initial',
              minWidth: '340px',
            }}
          >
            <div className="popup-table-container">
              <div className="popup-table-header" ref={headerRef}>
                <div className="popup-table-title">
                  {data.dataset_subtype_name ? (
                    <Pill
                      label={data.dataset_subtype_name}
                      className={DatasetSubtypeOperation[data.dataset_subtype_operation]?.className}
                    />
                  ) : (
                    <Icon name={data.dataset_type_name} tooltip={data.dataset_type_name} />
                  )}
                  <Link
                    className="link"
                    ref={titleRef}
                    style={{ minWidth: '100px' }}
                    to={`/systems/${data.system_id}/${data.datasource_id}/${data.dataset_group_id}/${item.nodeId}?tab=${getTabForUrl()}`}
                    onMouseEnter={e => showTooltip({ title: data.label })}
                    onMouseLeave={hideTooltip}
                  >
                    <span>{data.dataset_name}</span>
                  </Link>
                </div>
                <div className="popup-table-toolbar">
                  <SelectButtons
                    name="view_mode"
                    value={viewMode}
                    options={viewModes}
                    onChange={handleViewModeChange}
                    title="Select view mode"
                    design="slim"
                  />

                  <Checkbox
                    onChange={e => toggleShowDescription()}
                    checked={showDescription}
                    icon="small_descriptions"
                    title={showDescription ? 'Hide descriptions' : 'Show Descriptions'}
                  />

                  <div
                    className="popup-table-toggle-button"
                    onClick={e => togglePopup(false, item?.nodeId)}
                    onMouseEnter={e => showTooltip({ title: 'Close' })}
                    onMouseLeave={hideTooltip}
                  ></div>
                </div>
              </div>
              {showDescription && (
                <div className="popup-table-description" ref={descriptionRef}>
                  {data.dataset_description ? (
                    textareaToPlainText(data.dataset_description)
                  ) : (
                    <span className="no-result-text">No description available</span>
                  )}
                </div>
              )}
              <div className="popup-table-body">
                {viewMode && viewMode === 'fields' && (
                  <div className="scroll-container" ref={tableRef}>
                    <DataTable
                      columns={getFieldTableColumns()}
                      data={fields}
                      idColumn="field_id"
                      parentIdColumn="parent_field_id"
                      expandable={true}
                      editable={false}
                      loading={loadingTable}
                      paginated={false}
                      filterable={false}
                      hideToolbar={true}
                      className="slim bg-white"
                      onMouseEnterRow={onMouseEnterRow}
                      onMouseLeaveRow={onMouseLeaveRow}
                    />
                  </div>
                )}
                {viewMode && viewMode === 'lineage' && (
                  <div className="scroll-container" ref={tableRef}>
                    <div className="lineage-table">
                      <h3>
                        <Icon name="arrow_left" />
                        Upstream
                        <span className="thin-text">{upstreamLineage?.length}</span>
                      </h3>
                      <DataTable
                        columns={getUpstreamTableColumns()}
                        data={upstreamLineage}
                        header={false}
                        idColumn="id"
                        editable={false}
                        loading={loadingTable}
                        paginated={false}
                        filterable={false}
                        hideToolbar={true}
                        className="slim bg-white"
                        onMouseEnterRow={onMouseEnterRow}
                        onMouseLeaveRow={onMouseLeaveRow}
                        defaultSort={{ key: 'dataset_name', direction: 'asc' }}
                      />
                      <h3>
                        <Icon name="arrow_right" />
                        Downstream
                        <span className="thin-text">{downstreamLineage?.length}</span>
                      </h3>
                      <DataTable
                        columns={getDownstreamTableColumns()}
                        data={downstreamLineage}
                        header={false}
                        idColumn="id"
                        editable={false}
                        loading={loadingTable}
                        paginated={false}
                        filterable={false}
                        hideToolbar={true}
                        className="slim bg-white"
                        onMouseEnterRow={onMouseEnterRow}
                        onMouseLeaveRow={onMouseLeaveRow}
                        defaultSort={{ key: 'dataset_name', direction: 'asc' }}
                      />
                    </div>
                  </div>
                )}

                {viewMode && viewMode === 'relationships' && (
                  <div className="scroll-container" ref={tableRef}>
                    <DataTable
                      columns={getRelationshipsColumns()}
                      ref={tableRef}
                      data={datasetRelationships?.edges}
                      idColumn="id"
                      groupByColumn="id"
                      editable={false}
                      loading={loadingTable}
                      paginated={false}
                      filterable={false}
                      hideToolbar={true}
                      className="slim bg-white"
                      onMouseEnterRow={onMouseEnterRow}
                      onMouseLeaveRow={onMouseLeaveRow}
                    />
                  </div>
                )}
                {viewMode && viewMode === 'technical_definition' && (
                  <TechnicalDefinition
                    data={datasetDefinition}
                    showInlineToolbar={true}
                    //showLineNumbers={true}
                    language={technicalDefinitionLanguage(
                      data.datasource_type_code,
                      Asset.Dataset,
                      data.dataset_type_code
                    )}
                    ref={technicalDefinitionRef}
                  />
                )}
              </div>
            </div>
          </div>
        </Resizable>
      </Draggable>
    );
  }
);
